// imports
import type { ReadyStateEvent, SSE, SSEvent } from 'sse.js'

// components
import BaseAdapter from '../base'

// utils
import messages from './messages'
import XdcInstance from './instance'
import messageParser from './messageParser'

import utils from '@/utils/utils'
import type { QueryParams } from '@/utils/api/api'
import type {
    AdapterConfig,
    AdapterParams,
    InternalMessage,
    RawIncomingMessage,
} from '@/utils/ai-adapters/xdc/types'

type MessageRole = keyof typeof messages;

const SSE_CLOSED_STATE = 2;

// Helper to apply FormData fields to a parsed message object
const applyFormDataToParsedMessage = (parsedMessage: InternalMessage, form: FormData) => {
    parsedMessage.content = (form.get('message') as string) ?? ''
    const base64 = form.get('attachmentBase64') as string | null
    const source = form.get('attachmentSource') as string | null
    const fileUrlsRaw = form.get('file_urls') as string | null
    const previewUrlsRaw = form.get('preview_urls') as string | null
    if (base64) parsedMessage.attachmentBase64 = base64
    if (source) parsedMessage.attachmentSource = source
    if (fileUrlsRaw) {
        let urls: string[] = []
        try {
            const parsed = JSON.parse(fileUrlsRaw)
            if (Array.isArray(parsed)) urls = parsed.filter(Boolean)
        } catch (err) {
            urls = fileUrlsRaw.split(',').map(s => s.trim()).filter(Boolean)
            console.warn(`[XdcAdapter] Failed to parse file_urls as JSON, falling back to comma-separated string: ${fileUrlsRaw}`, err)
        }
        parsedMessage.file_urls = urls
    }
    if (previewUrlsRaw) {
        let urls: string[] = []
        try {
            const parsed = JSON.parse(previewUrlsRaw)
            if (Array.isArray(parsed)) urls = parsed.filter(Boolean)
        } catch (err) {
            urls = previewUrlsRaw.split(',').map(s => s.trim()).filter(Boolean)
            console.warn(`[XdcAdapter] Failed to parse preview_urls as JSON, falling back to comma-separated string: ${previewUrlsRaw}`, err)
        }
        parsedMessage.preview_urls = urls
    }
}

class XdcAdapter extends BaseAdapter {
    #instance: XdcInstance | undefined
    chat: InternalMessage[]
    updateMessageList: (updatedChat: InternalMessage[]) => Promise<void> | void = async () => { }

    constructor(config: AdapterConfig | undefined) {
        super('XdcAdapter', config) // makes this.assistant instance available
        this.chat = []
    }

    async init(updateMessageList: (messageList: InternalMessage[]) => Promise<void> = async () => { }) {
        this.#instance = new XdcInstance(
            this.endpoint,
            this.config?.endpointMapping as Record<string, string>
        )

        this.updateMessageList = updateMessageList || (async () => { })

        return
    }

    private isUnauthorizedError(error: unknown): boolean {
        if (!error || typeof error !== 'object') return false

        const { responseCode, status, statusCode } = error as { responseCode?: unknown; status?: number; statusCode?: number }

        if (/401/.test(String(responseCode))) return true

        return (status ?? statusCode) === 401
    }

    private async runCompletionWithParams(messageArg: string | FormData, assistantAnswerArg: InternalMessage, paramsToUse: AdapterParams) {
        if (this.config?.asStream) {
            return this.streamChatCompletions(
                messageArg as string,
                assistantAnswerArg,
                paramsToUse
            )
        }

        return this.defaultChatCompletions(
            messageArg as string,
            assistantAnswerArg,
            paramsToUse
        )
    }

    private async tryRunWithRetry(messageArg: string | FormData, assistantAnswerArg: InternalMessage, paramsArg: AdapterParams) {
        try {
            await this.runCompletionWithParams(messageArg, assistantAnswerArg, paramsArg)
        } catch (error) {
            const retryOnUnauthorized = paramsArg?.retryOnUnauthorized
            const alreadyRetried = paramsArg?._retryOnUnauthorized

            if (!alreadyRetried && typeof retryOnUnauthorized === 'function' && this.isUnauthorizedError(error)) {
                const refreshedParams = await retryOnUnauthorized()
                const retryParams = {
                    ...paramsArg,
                    ...refreshedParams,
                    _retryOnUnauthorized: true,
                }

                await this.runCompletionWithParams(messageArg, assistantAnswerArg, retryParams)
            } else {
                throw error
            }
        }
    }

    // `params` may be a promise (for example while the token is checked): the message and the empty answer (with its
    // status) show right away, the request waits for the params. Params that resolve to undefined cancel the request.
    async getChatCompletions(messageList: InternalMessage[], newMessage: string | FormData, role: MessageRole = 'user', params: AdapterParams | Promise<AdapterParams | undefined>, onlyAssistantMessage: boolean = false, isHandOver: boolean = false) {
        if (isHandOver) {
            this.chat = []
        } else {
            this.chat = messageList
        }
        const previousChat = this.chat

        // first, parse messages to visualise them in the chat
        const parsedMessage = this.parseMessage(role, newMessage)
        const assistantAnswer = this.parseMessage((messages['assistant'].role as MessageRole), '')

        // second, update the chat with the message
        if (onlyAssistantMessage) this.updateNewChat([assistantAnswer])
        else this.updateNewChat([parsedMessage, assistantAnswer])

        let resolvedParams: AdapterParams | undefined
        try {
            resolvedParams = await params
        } catch (error) {
            await this.cancelPendingAnswer(previousChat)
            throw error
        }

        if (!resolvedParams) {
            await this.cancelPendingAnswer(previousChat)
            return
        }

        await this.tryRunWithRetry(newMessage, assistantAnswer, resolvedParams)

        // last step, clear chat list
        this.chat = []
    }

    async defaultChatCompletions(message: string, answer: InternalMessage, params: AdapterParams) {
        const result = await this.#instance?.getChatCompletions(message, params)

        const parsedMessage = messageParser.parseAggregatedMessage(
            answer,
            result
        )

        return await this.updateNewChat(parsedMessage, true)
    }

    // Extracted stream processing into a helper to reduce cognitive complexity of the public method
    async _processStream(message: string, answer: InternalMessage, params: AdapterParams) {
        const stream: SSE | undefined = await this.#instance?.streamChatCompletions(message, params)
        if (!stream) throw new Error('SSE stream not created')

        return new Promise<void>((resolve, reject) => {
            let parsedMessage = { ...answer }

            // the handlers are defined below: cleanup only runs after them
            const cleanup = () => {
                try {
                    stream.removeEventListener('error', onError)
                } catch (_) { console.warn('Failed to remove SSE error listener', _) }
                try {
                    stream.removeEventListener('message', onMessage)
                } catch (_) { console.warn('Failed to remove SSE message listener', _) }
                try {
                    stream.removeEventListener('readystatechange', onReadyStateChange)
                } catch (_) { console.warn('Failed to remove SSE readystatechange listener', _) }
            }

            const onError = (event: SSEvent) => {
                console.error('SSE error', event)
                cleanup()
                reject(event)
            }

            const onMessage = async (event: SSEvent) => {
                const rawData = String(event.data ?? '').trim()
                if (!rawData) return

                try {
                    const choice: RawIncomingMessage = JSON.parse(rawData)
                    let isNewMessage = false

                    if (parsedMessage.messageId && choice.messageId)
                        isNewMessage = choice.messageId !== parsedMessage.messageId

                    if (isNewMessage)
                        parsedMessage = this.parseMessage((messages['assistant'].role as MessageRole), '')

                    parsedMessage = messageParser.parse(parsedMessage, choice)

                    await this.updateNewChat(parsedMessage, !isNewMessage)
                } catch (error) {
                    console.warn('[XdcAdapter] Failed to parse SSE event data:', rawData, error)
                    return
                }
            }

            const onReadyStateChange = async (event: ReadyStateEvent) => {
                if (event.readyState === SSE_CLOSED_STATE) {
                    parsedMessage.finishedMessage = true
                    await this.updateNewChat(parsedMessage, true)
                    cleanup()
                    resolve()
                }
            }

            stream.addEventListener('error', onError)
            stream.addEventListener('message', onMessage)
            stream.addEventListener('readystatechange', onReadyStateChange)
        })
    }

    async streamChatCompletions(message: string, answer: InternalMessage, params: AdapterParams) {
        try {
            await this._processStream(message, answer, params)
        } catch (error) {
            console.warn('streamChatCompletions() failed', error)

            const updatedChat = this.chat

            if (Array.isArray(updatedChat) && updatedChat.length > 0) {
                updatedChat[updatedChat.length - 1] = {
                    ...updatedChat[updatedChat.length - 1],
                    finishedMessage: true,
                }
            }

            await this.updateNewChat(updatedChat, true)

            throw error
        }

        return
    }

    async getAudioTranscription(audio: Blob, params: AdapterParams) {
        const result = await this.#instance?.getAudioTranscription(audio, params)

        return result
    }

    async getHistory(page = 0, params: AdapterParams) {
        if (params.chatID)
            return await this.#instance?.getChatHistory(page, params)

        return await this.#instance?.getAssistantHistory(page, params)
    }

    async getMetadata(params: AdapterParams) {
        const result = await this.#instance?.getMetadata(params)

        return result
    }

    async sendFeedback(userId: string, chatId: string, messageId: string, type: string, freeText: string, checkboxes: string[]) {
        const result = await this.#instance?.sendFeedback(
            userId,
            chatId,
            messageId,
            type,
            freeText,
            checkboxes
        )

        return result
    }

    async sendCustomCall(url: string, options: Record<string, unknown>, params: AdapterParams, query?: QueryParams) {
        const result = await this.#instance?.sendCustomCall(
            url,
            options,
            params,
            query
        )

        return result
    }

    /*************************************************************************************************************
     *                                        helper functions
     ************************************************************************************************************/

    parseMessage(role: MessageRole, message: unknown, args?: Record<string, unknown>): InternalMessage {
        const parsedMessage: InternalMessage = { ...messages[role], ...args }

        if (typeof message === 'string') {
            parsedMessage.content = message
        } else if (utils.isFormData(message)) {
            applyFormDataToParsedMessage(parsedMessage, message)
        } else {
            console.warn(`[${this.name}] message type not supported`)
        }

        return parsedMessage
    }

    // The request was not sent: the chat goes back to how it was before (without the waiting answer)
    private async cancelPendingAnswer(previousChat: InternalMessage[]) {
        if (Array.isArray(previousChat) && previousChat.length > 0) {
            await this.updateNewChat(previousChat, true)
        } else {
            // nothing to go back to: keep the message, but end the waiting
            const withoutAnswer = (this.chat || []).slice(0, -1)
            if (withoutAnswer.length > 0) {
                withoutAnswer[withoutAnswer.length - 1] = { ...withoutAnswer[withoutAnswer.length - 1], finishedMessage: true }
                await this.updateNewChat(withoutAnswer, true)
            }
        }
        this.chat = []
    }

    // can handle message & messageArrays <3
    async updateNewChat(message: InternalMessage | InternalMessage[], replaceChat = false) {
        let updatedChat: InternalMessage[] = JSON.parse(JSON.stringify(this.chat || []))

        if (Array.isArray(message)) {
            if (replaceChat) updatedChat = JSON.parse(JSON.stringify(message))
            else updatedChat.push(...message)
        } else {
            if (!replaceChat) updatedChat.push(message)
            else {
                updatedChat[updatedChat.length - 1] = message
            }
        }

        this.chat = updatedChat

        await this.updateMessageList(updatedChat)
    }
}

export default XdcAdapter
